<script lang="ts">
  import type { BookingDetails } from '$lib/types'
  import { t } from '$lib/translations'
  import { toast } from 'svelte-sonner'
  import { twMerge } from 'tailwind-merge'
  import * as Tooltip from '$lib/components/ui/tooltip'
  import { Info } from 'lucide-svelte'
  import { IN_OUT, NINJA_SERVICE_TYPES_NAMES, VEHICLE_TYPES } from '$lib/constants'
  import { debounce } from '$lib/helpers'

  let showMenu = false
  export let booking: BookingDetails
  export let showShadow = true
  export let showTitle: boolean = true
  export let userIsLogged = true
  export let loggedUserEmail: string | undefined = undefined
  export let isMap: boolean = false
  let className: string | undefined = undefined
  export { className as class }

  function toggleMenu() {
    showMenu = !showMenu
  }

  let justCancelled = false

  $: userIsOwner = loggedUserEmail && booking?.email && loggedUserEmail === booking.email
  const encodedBookingId = booking?.id ? btoa(booking.id as unknown as string) : ''
  const imageUrl = `data:image/png;base64,${booking.qr}`

  async function cancelBooking() {
    const bookingId = booking.id

    const res = await fetch(`/api/cancel-booking?booking=${bookingId}`, { method: 'POST' })
    if (!res.ok) {
      toast.error($t('errors.something_went_wrong'))
      return
    }

    const data = await res.json()
    if (data.success) {
      toast.success($t('mybooking.success_cancel_booking'))
      justCancelled = true
      showMenu = false
    } else {
      toast.error($t('errors.something_went_wrong'))
    }
  }
  const debouncedResendEmail = debounce(resendEmail, 500) // 500 ms de tiempo de espera

  async function resendEmail() {
    const bookingId  = booking.id

    try {
      const res = await fetch(`/api/resend-email?booking_id=${bookingId}`, { method: 'POST' })
      const data = await res.json()

      if (data.success) {
        toast.success($t('mybooking.success_resend_email'))
        showMenu = false
      } else {
        toast.error($t('errors.something_went_wrong'))
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error($t('errors.something_went_wrong'))
    }
  }

  async function debouncedResendEmailWrapper() {
    debouncedResendEmail()
  }
  const getServiceTypeByVehicle = (service: string, vehicle: number) => {
    switch (vehicle) {
      case VEHICLE_TYPES[5].id:
        if (service == NINJA_SERVICE_TYPES_NAMES.ATDISPOSAL) {
          return t.get('common.at_disposal') + ' ' + t.get('common.adapted_service')
        }
        return t.get('common.adapted_service')
      case VEHICLE_TYPES[1].id:
      case VEHICLE_TYPES[4].id:
        if (service == NINJA_SERVICE_TYPES_NAMES.ATDISPOSAL) {
          return t.get('common.at_disposal') + ' ' + t.get('common.luxury_service')
        }
        return t.get('common.luxury_service')
      default:
        switch (service) {
          case NINJA_SERVICE_TYPES_NAMES.SHUTTLE:
            return t.get('common.shuttle')
          case NINJA_SERVICE_TYPES_NAMES.PRIVATE:
            return t.get('common.private')
          case NINJA_SERVICE_TYPES_NAMES.ATDISPOSAL:
            return t.get('common.at_disposal')
          default:
            break
        }
    }
  }
</script>

<div class={twMerge('flex flex-col items-center justify-center w-5/5 lg:w-4/5 mx-auto', className)} data-test-id="my-booking-component">
  {#if showTitle}
    <h1 class="lg:text-4xl font-bold text-gray-800 my-8 text-center">
      {$t('mybooking.manage_modify_cancel_booking')}
    </h1>
  {/if}
  <div class={twMerge('bg-white shadow-lg rounded-none p-6 w-full flex lg:flex-col', !showShadow ? 'shadow-none' : '')}>
    <div class="flex justify-between items-center lg:border-b pb-4 flex-col lg:flex-row min-w-full lg:w-auto">
      <div class="flex flex-row justify-between min-w-full lg:justify-normal">
        <div class="basis-3/4">
          <div class="text-gray-600 text-sm" data-test-id="service-type">
            <span class="font-bold">
              {$t('mybooking.service')}:
            </span> <span class="text-orange-500 font-bold">{getServiceTypeByVehicle(booking.serviceType, booking.vehicleType)}</span>
          </div>
          <div class="text-gray-600 text-sm" data-test-id="booking-id">
            <span class="font-bold"> {$t('mybooking.booking_id')}:</span>
            {booking.id}
          </div>
          <div class="text-gray-600 text-sm" data-test-id="booking-date">
            <span class="font-bold">{$t('mybooking.booking_date')}:</span>
            {booking.date}
          </div>
          {#if booking.flightDate != undefined && booking.inOut != IN_OUT.OTHER && booking.serviceType != NINJA_SERVICE_TYPES_NAMES.ATDISPOSAL}
            <div class="text-gray-600 text-sm" data-test-id="flight-datetime">
              <span class="font-bold">
                {$t('mybooking.flight_arrival_time')}:
              </span>{booking.flightDate}
            </div>
          {/if}

          {#if booking.inOut != IN_OUT.IN}
            <div class="text-gray-600 text-sm" data-test-id="pickup_datetime">
              <span class="font-bold">
                {$t('mybooking.pickup_datetime')}:
              </span>
              {#if booking.pickupDate}
                {booking.pickupDate}
              {:else}
                {$t('mybooking.pending')}
                <Tooltip.Root>
                  <Tooltip.Trigger class="ml-1">
                    <Info class="cursor-pointer h-[13.5px] w-[13.5px]" />
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    {$t('mybooking.pick_up_tooltip')}
                  </Tooltip.Content>
                </Tooltip.Root>
              {/if}
            </div>
          {/if}
          {#if booking.endTime != undefined}
            <div class="text-gray-600 text-sm" data-test-id="flight-datetime">
              <span class="font-bold">
                {$t('forms.end_date')}:
              </span>{booking.endTime}
            </div>
          {/if}
          {#if booking.flightNumber}
            <div class="text-gray-600 text-sm" data-test-id="flight-number">
              <span class="font-bold"> {$t('mybooking.flight_number')}:</span>
              {booking.flightNumber}
            </div>
          {/if}
          <div class="text-gray-600 text-sm" data-test-id="pax">
            <span class="font-bold">
              {$t('mybooking.passengers')}:
            </span>{booking.pax}
          </div>
          <div class="text-gray-600 text-sm" data-test-id="extra-luggage">
            <span class="font-bold">{$t('mybooking.extra_luggage')}:</span>
            {booking.luggage ?? 0}
          </div>
          <div class="text-gray-600 text-sm" data-test-id="start_place">
            <span class="font-bold">{$t('common.start_place')}:</span>
            {booking.startPlace?.Text}
            {#if booking.startPlace?.Latitude && booking.startPlace?.Longitude}
              <a
                href={`//maps.google.com/maps?q=${booking.startPlace?.Latitude},${booking.startPlace?.Longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-blue-600"
              >
                ({booking.startPlace?.Latitude},{booking.startPlace?.Longitude})
              </a>
            {/if}
          </div>
          {#if booking.startMeetingPoint}
            <div class="text-gray-600 text-sm ml-3" data-test-id="start_place">
              <span class="font-bold">- {$t('common.meeting_point')}:</span>
              {booking.startMeetingPoint?.Text}
              <a
                href={`//maps.google.com/maps?q=${booking.startMeetingPoint?.Latitude},${booking.startMeetingPoint?.Longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-blue-600"
              >
                ({booking.startMeetingPoint?.Latitude},{booking.startMeetingPoint?.Longitude})
              </a>
            </div>
          {/if}
          {#if booking.endPlace}
            <div class="text-gray-600 text-sm" data-test-id="end_place">
              <span class="font-bold">{$t('common.end_place')}:</span>
              {booking.endPlace?.Text}
              {#if booking.endPlace?.Latitude && booking.endPlace?.Longitude}
                <a
                  href={`//maps.google.com/maps?q=${booking.endPlace?.Latitude},${booking.endPlace?.Longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-blue-600"
                >
                  ({booking.endPlace?.Latitude},{booking.endPlace?.Longitude})
                </a>
              {/if}
            </div>
          {/if}
          {#if booking.endMeetingPoint}
            <div class="text-gray-600 text-sm ml-3" data-test-id="start_place">
              <span class="font-bold">- {$t('common.meeting_point')}:</span>
              {booking.endMeetingPoint?.Text}
              {#if booking.endMeetingPoint}
                <a
                  href={`//maps.google.com/maps?q=${booking.endMeetingPoint?.Latitude},${booking.endMeetingPoint?.Longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-blue-600"
                >
                  ({booking.endMeetingPoint?.Latitude},{booking.endMeetingPoint?.Longitude})
                </a>
              {/if}
            </div>
          {/if}
          {#if booking.route}
            <div class="text-gray-600 text-sm ml-3" data-test-id="route">
              <span class="font-bold">- {$t('forms.route')}:</span>
              {booking.route}
            </div>
          {/if}
        </div>
        <div class="grid content-start basis-1/4 justify-items-end relative lg:flex lg:justify-center lg:items-center">
          <button
            class={twMerge('lg:hidden text-blue-500 hover:text-blue-600', justCancelled || isMap ? 'hidden' : '')}
            on:click={toggleMenu}
          >
            <img class="h-6" src="/common/menu-dots.svg" alt="Menu" />
          </button>
          <img class="hidden lg:flex max-h-40 lg:order-1" src={imageUrl} alt={$t('mybooking.bus_alt_img')} />
          {#if showMenu}
            <div class="flex flex-col space-y-4 bg-white shadow-md rounded-lg p-4 absolute top-[1rem] left-[-50px] z-50 min-w-[10rem]">
              <!-- Hidden until this features are implemented -->
              <!-- <div class="flex flex-col items-center">
                <button class="text-blue-500 hover:text-blue-600" data-test-id="download-qr-button">
                  <h2 class="text-sm font-semibold text-center text-gray-700 mt-2">
                    {$t('mybooking.download_qr')}
                  </h2>
                </button>
              </div>
              <div class="flex flex-col items-center">
                <button class="text-blue-500 hover:text-blue-600" data-test-id="download-invoice-button">
                  <h2 class="text-sm font-semibold text-center text-gray-700 mt-2">
                    {$t('mybooking.download_invoice')}
                  </h2>
                </button>
              </div>
              <div class="flex flex-col items-center">
                <button class="text-red-500 hover:text-red-600" data-test-id="edit-booking-button">
                  <h2 class="text-sm font-semibold text-center text-gray-700 mt-2">
                    {$t('mybooking.edit_booking')}
                  </h2>
                </button>
              </div> -->
              {#if encodedBookingId && !isMap}
                <div class="flex flex-col items-center">
                  <a
                    href={`/find-my-driver?booking=${encodedBookingId}`}
                    class="text-sm font-semibold text-center text-gray-700 mt-2 hover:text-red-600 text-red-500"
                    data-test-id="edit-booking-button"
                  >
                    {$t('common.find_my_driver')}
                  </a>
                </div>
              {/if}
              {#if booking.source == 'Web'}
                <div class="flex flex-col items-center">
                  <button class="text-red-500 hover:text-red-600" on:click={() => debouncedResendEmailWrapper()}>
                    <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                      {$t('mybooking.resend_email')}
                    </h2>
                  </button>
                </div>
              {/if}
              {#if userIsLogged && userIsOwner}
                <div class="flex flex-col items-center">
                  <button class="text-red-500 hover:text-red-600" data-test-id="cancel-booking-button" on:click={() => cancelBooking()}>
                    <h2 class="text-sm font-semibold text-center text-gray-700 mt-2">
                      {$t('mybooking.cancel_booking')}
                    </h2>
                  </button>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
      <div class="flex flex-row justify-center min-w-full lg:min-w-0 mt-5">
        <!-- Hidden until this feature is implemented. Change justify-end for an justify between -->
        <!-- <button class="text-blue-500 hover:text-blue-600">
          <img class="h-5" src="/common/add-to-wallet-icon.png" alt={$t('mybooking.download_qr')} />
        </button> -->

        <img class="max-h-36 order-2 lg:order-1" src={imageUrl} alt={$t('mybooking.bus_alt_img')} />
      </div>
      {#if encodedBookingId && !isMap}
        <div class="mt-4 w-full">
          <a href={`/find-my-driver?booking=${encodedBookingId}`}>
            <div class="lg:hidden flex flex-col items-center">
              <img class="h-10" src="/common/map-ico.svg" alt={$t('common.find_my_driver')} />
              <span class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                {$t('common.find_my_driver')}
              </span>
            </div>
          </a>
        </div>
      {/if}
    </div>

    <div class={twMerge('lg:flex pt-4 lg:border-t', justCancelled ? 'lg:hidden' : '')}>
      <div class="hidden lg:flex lg:justify-between lg:items-center pt-4 center lg:min-w-full">
        <div class="flex space-x-4">
          <!-- Hidden until this features are implemented -->
          <!-- <div class="flex">
              <button class="text-blue-500 hover:text-blue-600">
                <img class="h-10" src="/common/add-to-wallet-icon.png" alt={$t('mybooking.download_qr')} />
              </button>
            </div>
            <div class="flex flex-col items-center">
              <button class="text-blue-500 hover:text-blue-600">
                <img class="h-10" src="/common/qr-icon.svg" alt={$t('mybooking.download_qr')} />
              </button>
              <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                {$t('mybooking.download_qr')}
              </h2>
            </div>
            <div class="flex flex-col items-center">
              <button class="text-blue-500 hover:text-blue-600">
                <img class="h-10" src="/common/download-icon.svg" alt={$t('mybooking.download_invoice')} />
              </button>
              <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                {$t('mybooking.download_invoice')}
              </h2>
            </div> -->
          {#if encodedBookingId && !isMap}
            <a href={`/find-my-driver?booking=${encodedBookingId}`}>
              <div class="flex flex-col items-center">
                <img class="h-10" src="/common/map-ico.svg" alt={$t('common.find_my_driver')} />
                <span class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                  {$t('common.find_my_driver')}
                </span>
              </div>
            </a>
          {/if}
        </div>
        {#if booking.source == 'Web'}
          <div class="flex flex-col items-center">
            <button class="text-red-500 hover:text-red-600" on:click={() => debouncedResendEmailWrapper()}>
              <img class="h-10" src="/common/email.svg" alt={$t('mybooking.resend_email')} />
            </button>
            <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
              {$t('mybooking.resend_email')}
            </h2>
          </div>
        {/if}

        {#if userIsLogged && userIsOwner}
          {#if !booking.cancelled}
            <div class={'flex space-x-6'}>
              <!-- Hidden until this feature is implemented -->
              <!-- <div class="flex flex-col items-center">
                <button class="text-red-500 hover:text-red-600">
                  <img class="h-10" src="/common/edit-pencil-icon.svg" alt={$t('mybooking.edit_booking')} />
                </button>
                <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                  {$t('mybooking.edit_booking')}
                </h2>
              </div> -->
              <div class="flex flex-col items-center">
                <button class="text-red-500 hover:text-red-600" on:click={() => cancelBooking()}>
                  <img class="h-10" src="/common/cancel-icon.svg" alt={$t('mybooking.cancel_booking')} />
                </button>
                <h2 class="text-sm font-semibold text-center text-gray-700 mt-2 break-words max-w-[5rem]">
                  {$t('mybooking.cancel_booking')}
                </h2>
              </div>
            </div>
          {/if}
        {/if}
      </div>
    </div>
  </div>
</div>
